import React, { useEffect, useState } from 'react';
import { Box, Typography, TextField, FormControl, useTheme, useMediaQuery } from '@mui/material';
import { useSelector } from 'react-redux';
import { EnterpriseContext, EnterpriseContextState } from './EnterpriseContextInterfaces';
import { RootState } from '../../store';
import { IOrganization } from '../../common/types/organisation';
import { API_GRAPH_BASE } from '../../config/env';
import { httpService } from '../../utility/httpService';
import { httpConstants } from '../../constants';
import CreatableSelect from '../../common/components/CreatableMultiSelect';

interface CompanyFormProps {
  enterpriseContextPayload?: EnterpriseContextState;
  handleCompanyDetailsChange: <K extends keyof EnterpriseContext>(
    value: EnterpriseContext[K],
    fieldName: K
  ) => void;
  onSave?: () => void;
  onClear?: () => void;
  selectedIndustryId?: string | null;
  isAdmin?: boolean;
  /** Incremented when parent discards changes so local industry UI resyncs. */
  formResetKey?: number;
}

const CompanyForm: React.FC<CompanyFormProps> = ({
  enterpriseContextPayload,
  handleCompanyDetailsChange,
  selectedIndustryId = null,
  isAdmin = false,
  formResetKey = 0,
}) => {
  type DropdownOption = { label: string; value: string };

  const [industriesSuggestions, setIndustriesSuggestions] = useState<DropdownOption[]>([]);
  const [mappedIndustries, setMappedIndustries] = useState<DropdownOption[]>([]);
  const [userChangedIndustries, setUserChangedIndustries] = useState(false); // NEW STATE FLAG

  const companyId = localStorage.getItem('current_organization') || '';
  const organisation = useSelector((state: RootState) => state.organisation);
  const selectedOrg = organisation.find((org: IOrganization) => org.id === companyId);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));

  const fetchIndustries = async (): Promise<any> => {
    try {
      const url = `${API_GRAPH_BASE}/technology/industries`;

      const response = await httpService<any>(httpConstants.METHOD_TYPE.GET, url);

      if (!response) {
        throw new Error('Invalid response structure');
      }

      if (response) {
        const transformedResponse = response?.map((industry: any) => {
          return {
            label: industry?.name,
            value: industry?.id,
          };
        });
        setIndustriesSuggestions(transformedResponse);
      } else {
        return null;
      }
    } catch (err: any) {
      console.error('Error fetching solution by ID:', err);
      throw err;
    }
  };

  useEffect(() => {
    fetchIndustries();
  }, []);

  useEffect(() => {
    setUserChangedIndustries(false);
  }, [formResetKey]);

  useEffect(() => {
    if (userChangedIndustries) return; // PREVENT OVERRIDE AFTER USER REMOVAL
    const industries = enterpriseContextPayload?.enterpriseContext?.industry || [];
    const suggestions = industriesSuggestions;
    let mapped: DropdownOption[] = [];
    if (selectedIndustryId && suggestions.length > 0) {
      const match = suggestions.find((s) => s.value === selectedIndustryId);
      if (match) {
        setMappedIndustries([match]);
        return;
      }
    }
    console.log('Selected Industry ID:', selectedIndustryId);
    // Handle existing industry state (if no id selected through props)
    if (industries.length > 0 && suggestions.length > 0) {
      mapped = industries.map((val) => {
        const match = suggestions.find((s) => s.value === val);
        return match || { label: val, value: val };
      });
      setMappedIndustries(mapped);
    } else if (!selectedIndustryId) {
      setMappedIndustries([]);
    }
  }, [
    industriesSuggestions,
    selectedIndustryId,
    enterpriseContextPayload?.enterpriseContext?.industry,
    userChangedIndustries,
  ]);

  const handleIndustryChange = (newIndustries: DropdownOption[] | DropdownOption | null) => {
    if (isAdmin && Array.isArray(newIndustries) && newIndustries.length > 1) {
      return;
    }
    setUserChangedIndustries(true);
    if (!newIndustries || (Array.isArray(newIndustries) && newIndustries.length === 0)) {
      handleCompanyDetailsChange([], 'industry');
      setMappedIndustries([]);
      return;
    }

    if (Array.isArray(newIndustries)) {
      const values = newIndustries.map((item) => item.value);
      handleCompanyDetailsChange(values, 'industry');
      setMappedIndustries(newIndustries);
    }
  };

  const handleDescriptionChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    handleCompanyDetailsChange(event.target.value, 'description');
  };

  return (
    <Box
      sx={{
        maxWidth: isMobile ? '100%' : isTablet ? 800 : 1200,
        mx: 'auto',
        p: isMobile ? 2 : isTablet ? 3 : 4,
      }}
    >
      {/* Header */}
      <Typography
        variant={isMobile ? 'h6' : 'h5'}
        sx={{ mb: 3, fontWeight: 600, color: '#333', pb: 1 }}
      >
        Company Info
      </Typography>

      {/* Company Name */}
      <Box sx={{ mb: 3 }}>
        <Typography
          variant="subtitle1"
          sx={{ mb: 1.5, fontWeight: 500, color: '#555', fontSize: isMobile ? '0.9rem' : '1rem' }}
        >
          Company Name
        </Typography>
        <TextField
          fullWidth
          variant="outlined"
          placeholder="Company Name"
          value={selectedOrg?.name || ''}
          disabled
          sx={{
            '& .MuiOutlinedInput-root': {
              backgroundColor: '#f8f9fa',
              '& fieldset': { borderColor: '#e0e0e0' },
              '&:hover fieldset': { borderColor: 'primary' },
              '&.Mui-focused fieldset': { borderColor: 'primary' },
            },
            '& .MuiInputBase-input': {
              fontSize: isMobile ? '0.9rem' : '1rem',
              padding: isMobile ? '12px 14px' : '14px 16px',
            },
          }}
        />
      </Box>

      {/* Industry */}
      <Box sx={{ mb: 3 }}>
        <Typography
          variant="subtitle1"
          sx={{ mb: 1.5, fontWeight: 500, color: '#555', fontSize: isMobile ? '0.9rem' : '1rem' }}
        >
          Industry
        </Typography>

        <FormControl fullWidth>
          {industriesSuggestions.length > 0 ? (
            <CreatableSelect
              value={mappedIndustries}
              onChange={handleIndustryChange}
              placeholder="Add industries"
              size="small"
              width="100%"
              options={industriesSuggestions}
              // multiple={true}
              // isdisabled={!isAdmin && mappedIndustries.length >=1}
              // isclearable={isAdmin && mappedIndustries.length === 0}
              isdisabled={
                (!isAdmin && mappedIndustries.length === 0) ||
                (!isAdmin && mappedIndustries.length >= 1)
              }
            />
          ) : (
            <TextField
              disabled
              value="Loading industries..."
              size="small"
              fullWidth
              sx={{ backgroundColor: '#f8f9fa' }}
            />
          )}
        </FormControl>
      </Box>

      {/* Description */}
      <Box sx={{ mb: 4 }}>
        <Typography
          variant="subtitle1"
          sx={{ mb: 1.5, fontWeight: 500, color: '#555', fontSize: isMobile ? '0.9rem' : '1rem' }}
        >
          Description
        </Typography>
        <TextField
          fullWidth
          multiline
          rows={isMobile ? 4 : 6}
          variant="outlined"
          placeholder="Please add company details"
          value={enterpriseContextPayload?.enterpriseContext?.description || ''}
          onChange={handleDescriptionChange}
          sx={{
            '& .MuiOutlinedInput-root': {
              backgroundColor: '#ffffff',
              '& fieldset': { borderColor: '#e0e0e0' },
              '&:hover fieldset': { borderColor: 'primary' },
              '&.Mui-focused fieldset': { borderColor: 'primary' },
            },
            '& .MuiInputBase-input': { fontSize: isMobile ? '0.9rem' : '1rem' },
          }}
        />
      </Box>
    </Box>
  );
};

export default CompanyForm;
